// @flow

import React from 'react';
import { I18n, Translate } from 'react-redux-i18n';

import { getCurrentPcId, getCurrentShoppingCenter } from '../../../utils/urlUtils';
import { autoQuantityCurrencyString, autoQuantityMinutesString, roundValue } from '../../../utils/formatUtils';
import shoppingCenterApi from '../../../network/api/shoppingCenterApi';
import type { Event, FactsReduced, IsochronousZone, ShoppingCenter } from '../../../types/shoppingCenterType';
import { isochronousZones, kpiImage } from '../../../const';
import MarketingFact from './marketing/MarketingFact';
import parkingImg from '../../../img/factIcon/parking.png';
import peopleImg from '../../../img/factIcon/people.png';
import storeImg from '../../../img/factIcon/sc_fa_stores.png';
import carrefourImg from '../../../img/factIcon/sc_st_carrefour.png';
import screenImg from '../../../img/factIcon/sc_st_screens.png';
import budgetImg from '../../../img/factIcon/sc_ma_budget.png';
import surfaceImg from '../../../img/factIcon/surface.png';
import basketImg from '../../../img/marketingIcon/sc_ma_spending-basket.png';
import timeImg from '../../../img/marketingIcon/sc_ma_visit-time.png';
import SurfaceConvertor from '../../common/SurfaceConvertor';
import CurrencyConvertor from '../../common/CurrencyConvertor';
import KeyYears from './KeyYears';
import type { KeyFigureType } from '../../../types/marketingType';
import MarketingFactTooltip from './marketing/MarketingFactTooltip';
import MarketingFactColumn from './marketing/MarketingFactColumn';
import { getCurrentYear } from '../../../utils/dateUtils';

type Props = {
  baseShoppingCenterList: ShoppingCenter[],
  location: Location,
  isKeyFiguresLoading: boolean,
  keyFigures: KeyFigureType,
}

type State = {
  fact: ?FactsReduced,
  events: { [date: number]: Event[] },
  selectedKeyYear: ?Event,
  isochronousZones: IsochronousZone[],
  isochronousZonesIsLoading: boolean,
}

class FactFigure extends React.Component<Props, State> {
  state: State = {
    selectedKeyYear: null,
    fact: null,
    events: null,
    isochronousZones: [],
    isochronousZonesIsLoading: true,
  };

  fetchIsochronousZones = () => {
    // Fetch Isochronous zones from last year
    const yearToFetch = getCurrentYear() - 1;
    this.setState({ isochronousZonesIsLoading: true });
    shoppingCenterApi.fetchIsochronousZones(getCurrentPcId(), yearToFetch)
      .then(resp => resp.json())
      .then(isochronousZones => this.setState({ isochronousZones }))
      .finally(() => this.setState({ isochronousZonesIsLoading: false }));
  }

  computeEvents = (fact: FactsReduced) => (
    fact.events.reduce((acc: { [date: number]: Event[] }, value: Event) => {
      if (!acc[value.startDate]) {
        acc[value.startDate] = [value];
      } else {
        acc[value.startDate].push(value);
      }
      return acc;
    }, {})
  );

  fetchFacts = () => {
    shoppingCenterApi.fetchFacts(getCurrentPcId())
      .then(resp => resp.json())
      .then((fact: FactsReduced) => {
        const events = fact.events
          ? this.computeEvents(fact)
          : [];
        const selectedKeyYear = fact.events ? fact.events[0] : null;
        this.setState({
          fact,
          events,
          selectedKeyYear,
        });
      });
  }

  componentDidMount() {
    this.fetchFacts();
    this.fetchIsochronousZones();
  }

  render() {
    const shoppingCenter = getCurrentShoppingCenter(
      this.props.location.search,
      this.props.baseShoppingCenterList,
    );

    if (!shoppingCenter) {
      return false;
    }

    const displayGlaFromOm = this.state.fact && this.state.fact.glaInSquareMeters;
    const displayVisitCountFromOm = this.state.fact && this.state.fact.visitCount;
    const displayYearlyVisitCountFromOm = this.state.fact && this.state.fact.yearlyVisitCount;

    const isUS = shoppingCenter && shoppingCenter.countryCode === 'US';

    const isochronousZonesToDisplay = [
      isochronousZones['0-10'],
      isochronousZones['10-20'],
      isochronousZones['20-30'],
    ];
    const displayableIsochronousZones = !this.state.isochronousZonesIsLoading
      && this.state.isochronousZones
        .filter(zone => isochronousZonesToDisplay.includes(zone.iso))
        .sort((zone1, zone2) => zone1.iso.localeCompare(zone2.iso));

    return (
      <div className="shopping-center-fact">
        <div className="title">
          <Translate value="mallData.FACT_FIGURE_LABEL" />
        </div>
        <div className="facts-container">
          <MarketingFactColumn
            title={<Translate value="mallData.FACT_FIGURE.titles.general" />}
          >
            {
              this.state.fact && (
                <>
                  <MarketingFact
                    showTooltip={!isUS}
                    tooltipContent={
                      <MarketingFactTooltip
                        sourceText={<Translate value="tooltip.linkNumber" />}
                        definitionText={<Translate value="tooltip.storeNumber" />}
                      />
                    }
                    image={storeImg}
                    value={this.state.fact.storeAvailableCount}
                    label={<Translate value="mallData.FACT_FIGURE.STORE" />}
                  />
                  <MarketingFact
                    showTooltip={!isUS}
                    tooltipContent={
                      <MarketingFactTooltip
                        sourceText={<Translate value="tooltip.linkNumber" />}
                        definitionText={<Translate value="tooltip.salesR12M" />}
                      />
                    }
                    image={budgetImg}
                    value={<CurrencyConvertor value={this.state.fact.totalSales} />}
                    label={<Translate value="mallData.FACT_FIGURE.TOTAL_SALES" />}
                  />
                  <MarketingFact
                    showTooltip={!isUS}
                    tooltipContent={
                      <MarketingFactTooltip
                        sourceText={<Translate value="tooltip.linkNumber" />}
                        definitionText={<Translate value="tooltip.parkingSpace" />}
                      />
                    }
                    image={parkingImg}
                    value={this.state.fact.parkingSpaceCount}
                    label={<Translate value="mallData.FACT_FIGURE.PARKING_SPACE" />}
                  />
                  <MarketingFact
                    showTooltip={!isUS}
                    tooltipContent={
                      <MarketingFactTooltip
                        sourceText={<Translate value="tooltip.linkNumber" />}
                        definitionText={<Translate value="tooltip.screenNumber" />}
                      />
                    }
                    image={screenImg}
                    value={this.state.fact.cinemaScreenCount}
                    label={<Translate value="mallData.FACT_FIGURE.SCREENS" />}
                  />
                  {
                    this.state.fact.displayedFoodAnchor && (
                      <MarketingFact
                        showTooltip={!isUS}
                        tooltipContent={
                          <MarketingFactTooltip
                            sourceText={<Translate value="tooltip.linkNumber" />}
                            definitionText={<Translate value="tooltip.glaHyperMarket" />}
                          />
                        }
                        image={carrefourImg}
                        value={
                          <SurfaceConvertor
                            value={this.state.fact.displayedFoodAnchor.glaInSquareMeters}
                          />
                        }
                        label={this.state.fact.displayedFoodAnchor.storeName}
                      />
                    )
                  }
                </>
              )
            }
            <MarketingFact
              showTooltip={!isUS}
              tooltipContent={
                <MarketingFactTooltip
                  sourceText={<Translate value="tooltip.linkNumber" />}
                  definitionText={<Translate value="tooltip.glaNumber" />}
                />
              }
              image={surfaceImg}
              value={<SurfaceConvertor
                value={displayGlaFromOm ?
                  this.state.fact.glaInSquareMeters : shoppingCenter.glaInSquareMeters}
              />}
              label={<Translate value="mallData.FACT_FIGURE.GLA" />}
            />
            <MarketingFact
              showTooltip={!isUS}
              tooltipContent={
                <MarketingFactTooltip
                  sourceText={<Translate value="tooltip.data_overridden" />}
                  definitionText={<Translate value="tooltip.r12m" />}
                />
              }
              image={peopleImg}
              value={
                roundValue(
                  displayVisitCountFromOm ?
                    this.state.fact.visitCount : shoppingCenter.last12MonthVisitCount,
                  null,
                )
              }
              label={<Translate value="mallData.FACT_FIGURE.VISITS" />}
            />
            <MarketingFact
              showTooltip={!isUS}
              tooltipContent={
                <MarketingFactTooltip
                  sourceText={<Translate value="tooltip.data_overridden" />}
                  definitionText={<Translate value="tooltip.r12mannual" />}
                />
              }
              image={peopleImg}
              value={
                roundValue(
                  displayYearlyVisitCountFromOm ?
                    this.state.fact.yearlyVisitCount : shoppingCenter.yearlyVisitCount,
                  null,
                )
              }
              label={<Translate value="mallData.FACT_FIGURE.YEARLY_VISITS" />}
            />
            {
              !this.props.isKeyFiguresLoading
              && this.props.keyFigures.averageSpendBasket
              && (
                <MarketingFact
                  showTooltip={!isUS}
                  tooltipContent={
                    <MarketingFactTooltip
                      sourceText={<Translate value="tooltip.data_overridden" />}
                      definitionText={<Translate value="tooltip.average_spend_basket_content" />}
                    />
                  }
                  image={basketImg}
                  value={autoQuantityCurrencyString(this.props.keyFigures.averageSpendBasket)}
                  label={<Translate value="mallData.FACT_FIGURE.AVERAGE_SPEND_BASKET" />}
                />
              )
            }
            {
              !this.props.isKeyFiguresLoading
              && this.props.keyFigures.dwellTime
              && (
                <MarketingFact
                  showTooltip={!isUS}
                  tooltipContent={
                    <MarketingFactTooltip
                      sourceText={<Translate value="tooltip.data_overridden" />}
                      definitionText={<Translate value="tooltip.dwell_time_content" />}
                    />
                  }
                  image={timeImg}
                  value={autoQuantityMinutesString(this.props.keyFigures.dwellTime)}
                  label={<Translate value="mallData.FACT_FIGURE.DWELL_TIME" />}
                />
              )
            }
          </MarketingFactColumn>
          {
            shoppingCenter.kpi && shoppingCenter.kpi.length > 0 && (
              <MarketingFactColumn
                title={<Translate value="mallData.FACT_FIGURE.titles.assets" />}
              >
                {
                  shoppingCenter.kpi.map(kpi => (
                    <MarketingFact
                      showTooltip={!isUS}
                      key={`${kpi.type}-${kpi.value}-${kpi.label}`}
                      image={kpiImage[kpi.type]}
                      value={kpi.value}
                      label={kpi.label}
                    />
                  ))
                }
              </MarketingFactColumn>
            )
          }
          {
            (displayableIsochronousZones && displayableIsochronousZones.length !== 0) && (
              <MarketingFactColumn
                title={<Translate value="mallData.FACT_FIGURE.titles.isochronousZones" />}
                className="isochronous-zones"
              >
                {displayableIsochronousZones.map((isochronousZone: IsochronousZone) => (
                  <MarketingFact
                    key={isochronousZone.iso}
                    showTooltip={!isUS}
                    tooltipContent={
                      <MarketingFactTooltip
                        sourceText={<Translate value="tooltip.isochrone_source" year={isochronousZone.year} />}
                        definitionText={
                          <Translate
                            value="tooltip.isochrone_content"
                            range={I18n.t(`mallData.FACT_FIGURE.ISO_ZONES.${isochronousZone.iso}.range`)}
                          />
                        }
                      />
                    }
                    image={kpiImage.VISITS}
                    value={
                      <Translate
                        value="mallData.FACT_FIGURE.ISO_ZONE_TITLE"
                        iso={I18n.t(`mallData.FACT_FIGURE.ISO_ZONES.${isochronousZone.iso}.title`)}
                      />
                    }
                    label={<Translate
                      value="mallData.FACT_FIGURE.ISO_ZONE_INHABITANTS"
                      totalPopulation={roundValue(isochronousZone.totalPopulation)}
                    />}
                  />
                ))}
              </MarketingFactColumn>
            )
          }
        </div>
        {
          this.state.events
          && Object.keys(this.state.events).length > 0
          && (
            <KeyYears
              events={this.state.events}
              selectedKeyYear={this.state.selectedKeyYear}
              setSelectedKeyYear={keyYear => this.setState({ selectedKeyYear: keyYear })}
            />
          )
        }
      </div>
    );
  }
}

export default FactFigure;
